import { useState, useRef, useMemo, useEffect } from 'react';
import { FormattingToolbar } from './FormattingToolbar';
import { FieldsPanel } from './FieldsPanel';
import type {
  TemplateCategory,
  PageSize,
  Orientation,
  TemplateVariable,
} from '../hooks/useDocumentTemplates';
import { protectTableTags, restoreTableTags } from '../lib/protectTableTags';
import {
  tokensToChips,
  chipsToTokens,
  conditionalsToBlocks,
  blocksToConditionals,
  CHIP_CLASS,
  COND_CLASS,
} from '../lib/tokenChips';

/**
 * Physical page dimensions (in millimetres, portrait) for each supported page
 * size. The canvas width/height are derived from these + orientation so the
 * design surface matches the paper the PDF is rendered on.
 */
const PAGE_DIMENSIONS_MM: Record<PageSize, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A3: { width: 297, height: 420 },
  Letter: { width: 216, height: 279 },
  Legal: { width: 216, height: 356 },
};

/** Elements that cannot legally contain block-level children. */
const NO_BLOCK_CHILD_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'span',
  'a',
  'b',
  'i',
  'u',
  's',
  'strong',
  'em',
  'font',
  'label',
  'small',
  'sub',
  'sup',
  'code',
]);

/** Elements that make an inserted fragment "block-level". */
const BLOCK_TAGS = new Set([
  'div',
  'table',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'section',
  'blockquote',
  'pre',
]);

const tagOf = (node: Node): string => (node as Element).tagName?.toLowerCase() ?? '';

/** True when a fragment has a top-level block element (so it can't go inside a <p>). */
function isBlockFragment(fragment: DocumentFragment): boolean {
  return Array.from(fragment.children).some((el) => BLOCK_TAGS.has(el.tagName.toLowerCase()));
}

/** True when a node holds visible text or a non-text element (image, table, rule). */
function hasContent(node: Node): boolean {
  if ((node.textContent ?? '').trim().length > 0) return true;
  const el = node as Element | DocumentFragment;
  return typeof el.querySelector === 'function' && !!el.querySelector('img,table,hr,br');
}

/** Child-index path from `root` down to `node` (empty when node is root). */
function nodePath(root: Node, node: Node): number[] {
  const path: number[] = [];
  let current: Node | null = node;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return [];
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
    current = parent;
  }
  return path;
}

/** Walks a child-index path from `root`; null if the structure no longer matches. */
function nodeFromPath(root: Node, path: number[]): Node | null {
  let current: Node | null = root;
  for (const index of path) {
    current = current?.childNodes[index] ?? null;
    if (!current) return null;
  }
  return current;
}

/** Resolves canvas width/height in mm for a page size + orientation. */
function pageDimensionsMm(
  pageSize: PageSize,
  orientation: Orientation,
): { width: number; height: number } {
  const base = PAGE_DIMENSIONS_MM[pageSize] ?? PAGE_DIMENSIONS_MM.A4;
  return orientation === 'landscape' ? { width: base.height, height: base.width } : base;
}
import {
  getCategoryConfig,
  buildFirmHeaderHtml,
  buildTotalsHtml,
  buildNotesHtml,
  buildBillingEntityHtml,
  buildFooterBarHtml,
} from '../lib/categoryBlocks';
import {
  ReportColumnsModal,
  buildReportColumnsTable,
  type ReportColumn,
} from './ReportColumnsModal';
import { GroupByModal } from './GroupByModal';

interface VisualEditorProps {
  category: TemplateCategory;
  headerHtml: string;
  bodyHtml: string;
  footerHtml: string;
  onHeaderChange: (html: string) => void;
  onBodyChange: (html: string) => void;
  onFooterChange: (html: string) => void;
  /** Selected paper size — sizes the design canvas to match the output. */
  pageSize?: PageSize;
  /** Selected orientation — swaps canvas width/height when landscape. */
  orientation?: Orientation;
  /** Template fields, shown in the Fields panel and used to label token chips. */
  variables?: TemplateVariable[];
  /** For report templates: the columns the selected report produces. */
  reportColumns?: ReportColumn[];
  /** Whether the report's columns are still being discovered. */
  reportColumnsLoading?: boolean;
}

/** Which side panel is showing in the visual editor. */
type Panel = 'blocks' | 'fields';

type Section = 'header' | 'body' | 'footer';

interface Block {
  id: string;
  label: string;
  icon: string;
  html: string;
}

/**
 * Builds the block palette for the given template category.
 *
 * Category-dependent blocks (Firm Header, Totals, Notes, Footer Bar, Line
 * Items) reference the correct entity variables (e.g. `quote.total` for a Quote
 * rather than `invoice.total`). Blocks whose content the category does not
 * support (e.g. Totals for a Report, Line Items for a Letter) are omitted.
 */
function buildBlocks(category: TemplateCategory): Block[] {
  const cfg = getCategoryConfig(category);

  const blocks: Block[] = [
    { id: 'firm-header', label: 'Firm Header', icon: '🏢', html: buildFirmHeaderHtml(cfg) },
    {
      id: 'logo',
      label: 'Logo/Image',
      icon: '🖼',
      html: `<div style="margin:8px 0;"><img src="{{firm.logo_url}}" alt="Logo" style="max-height:60px;max-width:200px;" /></div>`,
    },
    {
      id: 'image-placeholder',
      label: 'Image (URL)',
      icon: '🔗',
      html: `<div style="margin:8px 0;"><img src="https://via.placeholder.com/200x60?text=Your+Logo" alt="Image" style="max-height:80px;" /></div>`,
    },
    {
      id: 'client-box',
      label: 'Client Info',
      icon: '👤',
      html: `<div style="margin:16px 0;"><h4 style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin:0 0 6px;">Bill To</h4><p style="margin:0;font-size:13px;font-weight:600;">{{client.business_name}}</p><p style="margin:2px 0;font-size:12px;">{{client.name}}</p><p style="margin:2px 0;font-size:12px;color:#6b7280;">{{client.address}}</p><p style="margin:2px 0;font-size:12px;color:#6b7280;">{{client.email}}</p></div>`,
    },
  ];

  if (cfg.hasLineItems) {
    blocks.push({
      id: 'line-items',
      label: 'Line Items Table',
      icon: '📋',
      html: `<table style="width:100%;border-collapse:collapse;margin:16px 0;"><thead><tr style="background:#f9fafb;"><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">#</th><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Description</th><th style="padding:10px 12px;text-align:center;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Qty</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Rate</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:600;color:#6b7280;border-bottom:2px solid #e5e7eb;">Amount</th></tr></thead><tbody>{{#each line_items}}<tr><td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">{{inc @index}}</td><td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">{{this.description}}</td><td style="padding:10px 12px;font-size:12px;text-align:center;border-bottom:1px solid #f3f4f6;">{{this.quantity}}</td><td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f3f4f6;">{{currency this.unit_price}}</td><td style="padding:10px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f3f4f6;">{{currency this.line_total}}</td></tr>{{/each}}</tbody></table>`,
    });
  }

  const totalsHtml = buildTotalsHtml(cfg);
  if (totalsHtml) {
    blocks.push({ id: 'totals', label: 'Totals', icon: '💰', html: totalsHtml });
  }

  const notesHtml = buildNotesHtml(cfg);
  if (notesHtml) {
    blocks.push({ id: 'notes', label: 'Notes', icon: '📝', html: notesHtml });
  }

  const billingEntityHtml = buildBillingEntityHtml(cfg);
  if (billingEntityHtml) {
    blocks.push({
      id: 'billing-entity',
      label: 'Billing Entity',
      icon: '🏦',
      html: billingEntityHtml,
    });
  }

  blocks.push(
    {
      id: 'heading',
      label: 'Heading',
      icon: 'H',
      html: `<h2 style="font-size:16px;font-weight:600;margin:16px 0 8px;color:#1f2937;">Section Title</h2>`,
    },
    {
      id: 'text',
      label: 'Text',
      icon: '¶',
      html: `<p style="font-size:12px;line-height:1.6;color:#374151;margin:8px 0;">Enter your text here.</p>`,
    },
    {
      id: 'two-col',
      label: '2 Columns',
      icon: '▥',
      html: `<div style="display:flex;gap:20px;margin:12px 0;"><div style="flex:1;"><p style="font-size:12px;">Left column</p></div><div style="flex:1;"><p style="font-size:12px;">Right column</p></div></div>`,
    },
    {
      id: 'divider',
      label: 'Divider',
      icon: '—',
      html: `<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />`,
    },
    { id: 'footer-bar', label: 'Footer Bar', icon: '📄', html: buildFooterBarHtml(cfg) },
  );

  return blocks;
}

const SECTION_CONFIG: Record<Section, { label: string; color: string; emptyText: string }> = {
  header: {
    label: 'Header',
    color: 'border-blue-400 bg-blue-50 dark:bg-blue-950/20',
    emptyText: 'Add firm logo, name, and document title here',
  },
  body: {
    label: 'Body',
    color: 'border-indigo-400 bg-white dark:bg-gray-800',
    emptyText: 'Add client info, line items, totals, and notes here',
  },
  footer: {
    label: 'Footer',
    color: 'border-gray-400 bg-gray-50 dark:bg-gray-850',
    emptyText: 'Add payment terms, contact info, or legal text here',
  },
};

/**
 * Visual template builder with Header / Body / Footer section switching.
 *
 * - Top: section tabs (Header, Body, Footer) to switch which part you're editing
 * - Left: block palette — click to add blocks to the active section
 * - Right: live preview showing the active section's content
 * - Click text in the preview to edit inline
 */
export function VisualEditor({
  category,
  headerHtml,
  bodyHtml,
  footerHtml,
  onHeaderChange,
  onBodyChange,
  onFooterChange,
  pageSize = 'A4',
  orientation = 'portrait',
  variables = [],
  reportColumns,
  reportColumnsLoading = false,
}: VisualEditorProps) {
  const [activeSection, setActiveSection] = useState<Section>('body');
  const [activePanel, setActivePanel] = useState<Panel>('blocks');
  // The placed block/field the user has selected in the canvas (for the
  // floating toolbar: delete / move / duplicate). Null when nothing is selected.
  const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);

  // expression -> friendly label, so {{client.name}} shows as a "Client Name"
  // chip. Built from the template's own field list; array-item fields are keyed
  // by their per-row form too (e.g. `line_items[].description` -> `this.description`).
  const tokenLabeler = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of variables) {
      map.set(v.key, v.label);
      const arrayItem = /^([A-Za-z_]\w*)\[\]\.(.+)$/.exec(v.key);
      if (arrayItem) map.set(`this.${arrayItem[2]}`, v.label);
    }
    return (expression: string): string => map.get(expression) ?? '';
  }, [variables]);

  // Canvas <- storage: protect table loops, chip value tokens, then wrap
  // if/unless conditionals in labelled boxes (after chipping, so the body and
  // the condition expression are already chips).
  const toCanvasHtml = (html: string): string =>
    conditionalsToBlocks(tokensToChips(protectTableTags(html), tokenLabeler), tokenLabeler);
  // Canvas -> storage: reverse of the above — unwrap conditional boxes, un-chip
  // value tokens, then unprotect table loops.
  const fromCanvasHtml = (html: string): string =>
    restoreTableTags(chipsToTokens(blocksToConditionals(html)));
  // Canvas dimensions follow the selected paper size + orientation so the
  // design surface matches the output PDF.
  const page = pageDimensionsMm(pageSize, orientation);
  const previewRef = useRef<HTMLDivElement>(null);

  // Last caret position inside the active editable canvas. Clicking a palette
  // block or toolbar button takes focus out of the canvas, so we remember where
  // the caret was and insert there instead of at the end of the section.
  const savedRangeRef = useRef<Range | null>(null);

  useEffect(() => {
    const onSelectionChange = () => {
      const root = previewRef.current;
      const selection = window.getSelection();
      if (!root || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (root.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  // A caret saved in one section means nothing in another; so does a selection.
  useEffect(() => {
    savedRangeRef.current = null;
    setSelectedEl(null);
  }, [activeSection]);

  // Highlight the currently selected block/field in the canvas.
  useEffect(() => {
    const root = previewRef.current;
    if (!root) return;
    const prev = root.querySelectorAll('[data-tpl-selected="true"]');
    prev.forEach((el) => el.removeAttribute('data-tpl-selected'));
    if (selectedEl && root.contains(selectedEl)) {
      selectedEl.setAttribute('data-tpl-selected', 'true');
    }
  }, [selectedEl, headerHtml, bodyHtml, footerHtml]);

  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [groupByModalOpen, setGroupByModalOpen] = useState(false);

  const isReport = category === 'report';

  // Sentinel ids for report blocks that open a picker modal instead of inserting
  // fixed HTML.
  const REPORT_COLUMNS_BLOCK_ID = 'report-columns';
  const GROUP_BY_BLOCK_ID = 'group-by';

  // Blocks are rebuilt whenever the category changes so inserted variables
  // (totals, notes, header meta) match the selected document type. Report
  // templates get extra "Report Columns" and "Group By" blocks that open modals.
  const blocks = useMemo(() => {
    const base = buildBlocks(category);
    if (category === 'report') {
      base.push(
        { id: REPORT_COLUMNS_BLOCK_ID, label: 'Report Columns', icon: '▦', html: '' },
        { id: GROUP_BY_BLOCK_ID, label: 'Group By', icon: '🗂', html: '' },
      );
    }
    return base;
  }, [category]);

  // Get current section's HTML and setter
  const getCurrentHtml = (): string => {
    switch (activeSection) {
      case 'header':
        return headerHtml || '';
      case 'body':
        return bodyHtml || '';
      case 'footer':
        return footerHtml || '';
    }
  };

  const setCurrentHtml = (html: string) => {
    switch (activeSection) {
      case 'header':
        onHeaderChange(html);
        break;
      case 'body':
        onBodyChange(html);
        break;
      case 'footer':
        onFooterChange(html);
        break;
    }
  };

  /** Puts the caret back at a remembered position after the section re-renders. */
  const restoreCaret = (caret: { path: number[]; offset: number }) => {
    const root = previewRef.current;
    if (!root) return;
    const container = nodeFromPath(root, caret.path);
    if (!container) return;
    const max =
      container.nodeType === Node.TEXT_NODE
        ? (container.textContent?.length ?? 0)
        : container.childNodes.length;
    const range = document.createRange();
    range.setStart(container, Math.min(caret.offset, max));
    range.collapse(true);
    root.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRangeRef.current = range.cloneRange();
  };

  /**
   * Inserts HTML into the active section at the caret the user last placed in
   * the canvas (replacing any selected text), and leaves the caret just after
   * the inserted content so the next insert continues from there.
   *
   * Falls back to appending at the end of the section when there is no usable
   * caret — e.g. the section is still empty, or the user hasn't clicked into
   * the canvas yet.
   */
  const insertHtml = (html: string) => {
    const root = previewRef.current;
    const saved = savedRangeRef.current;

    if (!root || !saved || !root.contains(saved.commonAncestorContainer)) {
      setCurrentHtml(getCurrentHtml() + '\n' + html);
      return;
    }

    const range = saved.cloneRange();
    range.deleteContents();
    // Protect table loop tags and render value tokens as chips before the
    // browser parses the block, so a line-items table keeps its {{#each}} and
    // inserted fields appear as chips rather than raw {{…}}.
    const fragment = range.createContextualFragment(toCanvasHtml(html));
    const lastNode = fragment.lastChild;

    // Block content (headings, tables, dividers, …) can't legally sit inside a
    // paragraph/heading/inline element — the HTML parser would silently
    // restructure it on re-render and the caret would end up in the wrong
    // place. So find the outermost such ancestor of the caret and split it
    // there: head stays above, the block goes in the middle, the tail follows.
    let splitTarget: Element | null = null;
    if (isBlockFragment(fragment)) {
      let node: Node | null = range.startContainer;
      while (node && node !== root) {
        if (node.nodeType === Node.ELEMENT_NODE && NO_BLOCK_CHILD_TAGS.has(tagOf(node))) {
          splitTarget = node as Element;
        }
        node = node.parentNode;
      }
    }

    if (splitTarget && splitTarget.parentNode) {
      const parent = splitTarget.parentNode;
      // Everything from the caret to the end of the target becomes the tail.
      const tailRange = document.createRange();
      tailRange.setStart(range.startContainer, range.startOffset);
      tailRange.setEndAfter(splitTarget);
      const tail = tailRange.extractContents();

      parent.insertBefore(fragment, splitTarget.nextSibling);
      if (hasContent(tail) && lastNode) {
        parent.insertBefore(tail, lastNode.nextSibling);
      }
      // Caret at the very start leaves an empty shell behind — drop it.
      if (!hasContent(splitTarget)) parent.removeChild(splitTarget);
    } else {
      range.insertNode(fragment);
    }

    // Caret goes just after the inserted content. Use a fresh range: the one
    // above may have been disturbed by extracting/inserting nodes.
    const caretRange = document.createRange();
    if (lastNode) caretRange.setStartAfter(lastNode);
    else caretRange.setStart(root, root.childNodes.length);
    caretRange.collapse(true);

    // Remember the caret as a path: React rewrites the section's HTML when the
    // state updates, which would otherwise drop the caret.
    const caret = {
      path: nodePath(root, caretRange.startContainer),
      offset: caretRange.startOffset,
    };
    setCurrentHtml(fromCanvasHtml(root.innerHTML));
    window.setTimeout(() => restoreCaret(caret), 0);
  };

  /**
   * Inserts a template field at the caret. Array-item fields (`line_items[].x`)
   * become a loop so they repeat per row; everything else is a single token
   * (rendered as a chip by `insertHtml`). Formatting (currency/date) matches the
   * code-mode inserter.
   */
  const insertField = (variable: TemplateVariable) => {
    const arrayItem = /^([A-Za-z_]\w*)\[\]\.(.+)$/.exec(variable.key);
    if (arrayItem) {
      const [, arrayName, field] = arrayItem;
      const expr =
        variable.type === 'currency'
          ? `{{currency this.${field}}}`
          : variable.type === 'date'
            ? `{{formatDate this.${field}}}`
            : `{{this.${field}}}`;
      insertHtml(`{{#each ${arrayName}}}<div>${expr}</div>{{/each}}`);
      return;
    }
    const expr =
      variable.type === 'currency'
        ? `{{currency ${variable.key}}}`
        : variable.type === 'date'
          ? `{{formatDate ${variable.key}}}`
          : `{{${variable.key}}}`;
    insertHtml(expr);
  };

  const addBlock = (block: Block) => {
    // These report blocks open a picker modal instead of inserting fixed HTML.
    if (block.id === REPORT_COLUMNS_BLOCK_ID) {
      setColumnsModalOpen(true);
      return;
    }
    if (block.id === GROUP_BY_BLOCK_ID) {
      setGroupByModalOpen(true);
      return;
    }
    insertHtml(block.html);
    setSelectedBlock(block.id);
    setTimeout(() => setSelectedBlock(null), 600);
  };

  const handleInsertReportColumns = (cols: ReportColumn[]) => {
    insertHtml(buildReportColumnsTable(cols));
    setColumnsModalOpen(false);
    setSelectedBlock(REPORT_COLUMNS_BLOCK_ID);
    setTimeout(() => setSelectedBlock(null), 600);
  };

  const handleInsertGroupBy = (html: string) => {
    insertHtml(html);
    setGroupByModalOpen(false);
    setSelectedBlock(GROUP_BY_BLOCK_ID);
    setTimeout(() => setSelectedBlock(null), 600);
  };

  const handlePreviewEdit = () => {
    if (previewRef.current) {
      // The canvas holds table loop tags as comments and value tokens as chips;
      // store the plain Handlebars form.
      setCurrentHtml(fromCanvasHtml(previewRef.current.innerHTML));
    }
  };

  // ─── Selecting a placed block/field (floating toolbar: option D) ───────────

  /** The nearest selectable unit around a node: a token chip, or a top-level block. */
  const selectableAncestor = (node: Node | null): HTMLElement | null => {
    const root = previewRef.current;
    if (!root) return null;
    let el = (
      node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement
    ) as HTMLElement | null;
    // A token chip is itself the unit.
    const chip = el?.closest(`.${CHIP_CLASS}`) as HTMLElement | null;
    if (chip && root.contains(chip)) return chip;
    // Clicking the conditional's own frame (not its editable body) selects the
    // whole condition so it can be deleted/moved as one unit. Clicking inside
    // the body falls through to normal text editing / block selection.
    if (el?.classList.contains(COND_CLASS) && root.contains(el)) return el;
    // Otherwise walk up to the direct child of the editable root (the "block"),
    // but never past a conditional wrapper (its body edits in place).
    while (el && el.parentElement && el.parentElement !== root) {
      if (el.parentElement.classList.contains(COND_CLASS)) return el;
      el = el.parentElement;
    }
    return el && root.contains(el) && el !== root ? el : null;
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    const target = selectableAncestor(e.target as Node);
    setSelectedEl(target);
  };

  /** Writes the current canvas DOM back to state and re-syncs selection. */
  const commitCanvas = () => {
    const root = previewRef.current;
    if (root) setCurrentHtml(fromCanvasHtml(root.innerHTML));
  };

  const deleteSelected = () => {
    if (!selectedEl || !previewRef.current?.contains(selectedEl)) return;
    selectedEl.remove();
    setSelectedEl(null);
    commitCanvas();
  };

  const duplicateSelected = () => {
    if (!selectedEl || !previewRef.current?.contains(selectedEl)) return;
    selectedEl.after(selectedEl.cloneNode(true));
    commitCanvas();
  };

  const moveSelected = (direction: 'up' | 'down') => {
    if (!selectedEl || !previewRef.current?.contains(selectedEl)) return;
    const sibling =
      direction === 'up' ? selectedEl.previousElementSibling : selectedEl.nextElementSibling;
    if (!sibling) return;
    if (direction === 'up') sibling.before(selectedEl);
    else sibling.after(selectedEl);
    commitCanvas();
  };

  const handleInsertImage = (url: string) => {
    const imgHtml = `<img src="${url}" alt="Image" style="max-width:100%;max-height:120px;margin:8px 0;" />`;
    // Same caret-aware insert as blocks. The old execCommand call only worked
    // while the canvas still had focus, which it doesn't after the toolbar is
    // clicked, so the image could be lost.
    insertHtml(imgHtml);
  };

  const clearSection = () => {
    if (window.confirm(`Clear the ${activeSection} section?`)) {
      setCurrentHtml('');
    }
  };

  const currentHtml = getCurrentHtml();
  const config = SECTION_CONFIG[activeSection];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Section Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex gap-1">
          {(Object.keys(SECTION_CONFIG) as Section[]).map((section) => {
            const sectionHtml =
              section === 'header' ? headerHtml : section === 'body' ? bodyHtml : footerHtml;
            const hasContent = (sectionHtml || '').trim().length > 0;
            return (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`relative rounded-md px-4 py-1.5 text-xs font-semibold transition-all ${
                  activeSection === section
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {SECTION_CONFIG[section].label}
                {hasContent && activeSection !== section && (
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {currentHtml.trim() && (
            <button
              onClick={clearSection}
              className="text-xs text-red-500 hover:text-red-700 dark:text-red-400"
              title={`Clear ${activeSection}`}
            >
              Clear
            </button>
          )}
          <span className="text-xs text-gray-400">
            Editing: <strong>{config.label}</strong>
          </span>
        </div>
      </div>

      <div className="flex h-[560px]">
        {/* Left rail: switch between ready-made Blocks and individual Fields */}
        <div className="flex w-56 shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex gap-1 border-b border-gray-200 p-2 dark:border-gray-700">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setActivePanel('blocks')}
              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                activePanel === 'blocks'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Blocks
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setActivePanel('fields')}
              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                activePanel === 'fields'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Fields
            </button>
          </div>

          {activePanel === 'fields' ? (
            <div className="min-h-0 flex-1 overflow-hidden p-3">
              <FieldsPanel variables={variables} onInsert={insertField} />
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              <div className="space-y-1.5">
                {blocks.map((block) => (
                  <button
                    key={block.id}
                    // Don't let the click pull focus out of the canvas, or the
                    // caret the block should be inserted at would be lost.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addBlock(block)}
                    className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-medium transition-all ${
                      selectedBlock === block.id
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:border-indigo-500'
                    }`}
                  >
                    <span className="text-base">{block.icon}</span>
                    {block.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-700">
                <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <li>Click a block to drop it at the cursor</li>
                  <li>Use the Fields tab to insert data (e.g. Client Name)</li>
                  <li>Click a block or field in the page to move, copy or delete it</li>
                  <li>Switch Header / Body / Footer with the tabs above</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Live Preview */}
        <div className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-gray-900">
          {/* Formatting Toolbar */}
          <FormattingToolbar onInsertImage={handleInsertImage} />

          {/* Selection toolbar — appears when a placed block/field is clicked. */}
          {selectedEl && (
            <div className="flex items-center gap-2 border-b border-indigo-200 bg-indigo-50 px-3 py-1.5 dark:border-indigo-800 dark:bg-indigo-900/30">
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                {selectedEl.classList.contains(CHIP_CLASS) ? 'Field selected' : 'Block selected'}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <SelToolBtn label="Move up" onClick={() => moveSelected('up')}>
                  ↑
                </SelToolBtn>
                <SelToolBtn label="Move down" onClick={() => moveSelected('down')}>
                  ↓
                </SelToolBtn>
                <SelToolBtn label="Duplicate" onClick={duplicateSelected}>
                  ⧉
                </SelToolBtn>
                <SelToolBtn label="Delete" onClick={deleteSelected} danger>
                  🗑
                </SelToolBtn>
                <SelToolBtn label="Deselect" onClick={() => setSelectedEl(null)}>
                  ✕
                </SelToolBtn>
              </div>
            </div>
          )}

          {/* Preview Canvas */}
          <div className="flex-1 overflow-y-auto bg-gray-100 p-6 dark:bg-gray-900">
            {/* Styling for token chips and the selected-element highlight. Scoped
                to this canvas; it does not affect the generated PDF. */}
            <style>{`
              .${CHIP_CLASS} {
                display: inline-block;
                padding: 0 6px;
                margin: 0 1px;
                border-radius: 9999px;
                background: #eef2ff;
                color: #4338ca;
                border: 1px solid #c7d2fe;
                font-size: 0.85em;
                line-height: 1.5;
                white-space: nowrap;
                user-select: none;
                cursor: pointer;
              }
              [data-tpl-selected="true"] {
                outline: 2px solid #6366f1;
                outline-offset: 1px;
                border-radius: 2px;
              }
              /* Conditional region: a faint dashed frame so the user can tell a
                 block only prints conditionally — with no caption text. */
              .${COND_CLASS} {
                border: 1px dashed #c7d2fe;
                border-radius: 4px;
                padding: 2px 4px;
                margin: 2px 0;
              }
              .${COND_CLASS} > .tpl-cond-body { outline: none; }
              .tpl-cond-else {
                border-top: 1px dashed #c7d2fe;
                margin: 4px 0;
                height: 0;
              }
            `}</style>
            {/* Full document preview with active section highlighted.
                The page represents white paper, so it stays light in dark mode
                too — otherwise the template's own (dark, print-oriented) text
                colors render dark-on-dark and become unreadable. Forcing the
                page white here is also WYSIWYG-accurate with the generated PDF. */}
            <div
              className="mx-auto rounded bg-white text-gray-900 shadow-sm"
              style={{ width: `${page.width}mm`, minHeight: `${page.height}mm`, maxWidth: '100%' }}
            >
              {/* Header preview (dimmed if not active) */}
              <div
                className={`rounded-t border-l-4 p-4 transition-opacity ${
                  activeSection === 'header'
                    ? 'border-blue-400 bg-blue-50/50 opacity-100'
                    : 'border-transparent bg-gray-50 opacity-50'
                }`}
                onClick={() => setActiveSection('header')}
                role="button"
                tabIndex={0}
                aria-label="Edit header"
              >
                <div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-blue-400">
                  Header
                </div>
                {headerHtml ? (
                  activeSection === 'header' ? (
                    <div
                      ref={activeSection === 'header' ? previewRef : undefined}
                      contentEditable
                      suppressContentEditableWarning
                      onClick={handleCanvasClick}
                      onBlur={handlePreviewEdit}
                      className="min-h-[40px] outline-none"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(headerHtml) }}
                    />
                  ) : (
                    <div
                      className="min-h-[40px]"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(headerHtml) }}
                    />
                  )
                ) : (
                  <p className="text-xs italic text-gray-400">{SECTION_CONFIG.header.emptyText}</p>
                )}
              </div>

              {/* Body preview */}
              <div
                className={`border-l-4 p-4 transition-opacity ${
                  activeSection === 'body'
                    ? 'border-indigo-400 bg-white opacity-100'
                    : 'border-transparent bg-white opacity-50'
                }`}
                onClick={() => setActiveSection('body')}
                role="button"
                tabIndex={0}
                aria-label="Edit body"
              >
                <div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-indigo-400">
                  Body
                </div>
                {bodyHtml ? (
                  activeSection === 'body' ? (
                    <div
                      ref={activeSection === 'body' ? previewRef : undefined}
                      contentEditable
                      suppressContentEditableWarning
                      onClick={handleCanvasClick}
                      onBlur={handlePreviewEdit}
                      className="min-h-[200px] outline-none"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(bodyHtml) }}
                    />
                  ) : (
                    <div
                      className="min-h-[200px]"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(bodyHtml) }}
                    />
                  )
                ) : (
                  <p className="text-xs italic text-gray-400">{SECTION_CONFIG.body.emptyText}</p>
                )}
              </div>

              {/* Footer preview */}
              <div
                className={`rounded-b border-l-4 p-4 transition-opacity ${
                  activeSection === 'footer'
                    ? 'border-gray-400 bg-gray-50 opacity-100'
                    : 'border-transparent bg-gray-50 opacity-50'
                }`}
                onClick={() => setActiveSection('footer')}
                role="button"
                tabIndex={0}
                aria-label="Edit footer"
              >
                <div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-gray-400">
                  Footer
                </div>
                {footerHtml ? (
                  activeSection === 'footer' ? (
                    <div
                      ref={activeSection === 'footer' ? previewRef : undefined}
                      contentEditable
                      suppressContentEditableWarning
                      onClick={handleCanvasClick}
                      onBlur={handlePreviewEdit}
                      className="min-h-[30px] outline-none"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(footerHtml) }}
                    />
                  ) : (
                    <div
                      className="min-h-[30px]"
                      dangerouslySetInnerHTML={{ __html: toCanvasHtml(footerHtml) }}
                    />
                  )
                ) : (
                  <p className="text-xs italic text-gray-400">{SECTION_CONFIG.footer.emptyText}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isReport && (
        <>
          <ReportColumnsModal
            open={columnsModalOpen}
            columns={reportColumns ?? []}
            loading={reportColumnsLoading}
            onClose={() => setColumnsModalOpen(false)}
            onInsert={handleInsertReportColumns}
          />
          <GroupByModal
            open={groupByModalOpen}
            columns={reportColumns ?? []}
            loading={reportColumnsLoading}
            onClose={() => setGroupByModalOpen(false)}
            onInsert={handleInsertGroupBy}
          />
        </>
      )}
    </div>
  );
}

/** A button in the selection toolbar (move / duplicate / delete / deselect). */
function SelToolBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // Keep the canvas selection intact while clicking the toolbar.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex h-6 min-w-[1.5rem] items-center justify-center rounded px-1.5 text-xs transition-colors ${
        danger
          ? 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/40'
          : 'text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-900/50'
      }`}
    >
      {children}
    </button>
  );
}
